import React, { useState } from 'react';
import {
  LogOut,
  ChevronDown,
  Menu,
  RefreshCw,
  Hash,
  ShieldCheck,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Header as DesignSystemHeader,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Separator,
} from '@roger/ui-components';
import ProductUpdatesButton from './ProductUpdatesButton';
import SupportButton from './SupportButton';
import { useToast } from '../hooks/useToastContext';
import { ESignaturePasswordDialog } from './signing/ESignaturePasswordDialog';

const Header: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isESignatureDialogOpen, setIsESignatureDialogOpen] = useState(false);
  const {
    user,
    logout,
    toggleSidebar,
    isSidebarCollapsed,
    isMobileSidebarOpen,
    toggleMobileSidebar,
    hasPermission,
    forceRefreshWorkosSession,
    showQuestionIds,
    toggleShowQuestionIds,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      logout: state.logout,
      toggleSidebar: state.toggleSidebar,
      isSidebarCollapsed: state.isSidebarCollapsed,
      isMobileSidebarOpen: state.isMobileSidebarOpen,
      toggleMobileSidebar: state.toggleMobileSidebar,
      hasPermission: state.hasPermission,
      forceRefreshWorkosSession: state.forceRefreshWorkosSession,
      showQuestionIds: state.showQuestionIds,
      toggleShowQuestionIds: state.toggleShowQuestionIds,
    }))
  );

  const isAdmin = hasPermission('admin:super-user');
  const { success: showSuccess, error: showError } = useToast();

  const handleReloadSession = async () => {
    setIsRefreshing(true);
    try {
      const didReload = await forceRefreshWorkosSession();
      if (didReload) {
        showSuccess('Session reloaded');
      } else {
        showError(
          'Reload failed',
          'Could not reload your session. Please try again.'
        );
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Helper to get initials
  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const actions = (
    <div className='flex items-center gap-2 sm:gap-4'>
      <ProductUpdatesButton />
      <SupportButton />

      {/* User Menu */}
      <Popover>
        <PopoverTrigger asChild>
          <button className='flex items-center gap-1 px-1 py-0.5 rounded-lg hover:bg-accent focus:outline-none'>
            <div className='h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold'>
              {getInitials(user?.name)}
            </div>
            <div className='hidden sm:block text-left'>
              <div className='text-sm font-medium leading-tight'>
                {user?.name || 'User'}
              </div>
            </div>
            <ChevronDown className='h-4 w-4 text-muted-foreground ml-1' />
          </button>
        </PopoverTrigger>
        <PopoverContent align='end' className='w-64 p-0'>
          <div className='p-2 pb-1 flex items-center gap-2'>
            <div>
              <div className='font-semibold text-base'>
                {user?.name || 'User'}
              </div>
              <div className='text-xs text-muted-foreground'>
                {user?.email || 'user@roger.com'}
              </div>
            </div>
          </div>
          <Separator className='my-2' />
          <div className='px-2'>
            <Button
              variant='ghost'
              className='w-full justify-start'
              onClick={() => setIsESignatureDialogOpen(true)}
            >
              <ShieldCheck className='h-4 w-4' /> Electronic Signature
            </Button>
          </div>
          {isAdmin && (
            <>
              <Separator className='my-2' />
              <div className='px-2'>
                <Button
                  variant='ghost'
                  className='w-full justify-start'
                  onClick={handleReloadSession}
                  disabled={isRefreshing}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                  />
                  {isRefreshing ? 'Reloading…' : 'Reload Session'}
                </Button>
                <Button
                  variant='ghost'
                  className='w-full justify-start'
                  onClick={toggleShowQuestionIds}
                >
                  <Hash className='h-4 w-4' />
                  {showQuestionIds ? 'Hide QIDs' : 'Show QIDs'}
                </Button>
              </div>
            </>
          )}
          <Separator className='my-2' />
          <div className='px-2 pb-2'>
            <Button
              variant='ghost'
              className='w-full justify-start text-destructive'
              onClick={logout}
            >
              <LogOut className='h-4 w-4' /> Sign Out
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ESignaturePasswordDialog
        open={isESignatureDialogOpen}
        signerEmail={user?.email ?? null}
        onClose={() => setIsESignatureDialogOpen(false)}
      />
    </div>
  );

  return (
    <DesignSystemHeader
      className='h-16 shrink-0 py-0 [&>div]:h-full'
      title={
        <>
          {/* Mobile: open the off-canvas sidebar */}
          <button
            type='button'
            onClick={toggleMobileSidebar}
            aria-expanded={isMobileSidebarOpen}
            aria-controls='mobile-navigation'
            aria-label={isMobileSidebarOpen ? 'Close menu' : 'Open menu'}
            className='md:hidden z-50 h-8 w-8 rounded-full flex items-center justify-center transition-colors duration-200 shadow-2xl border bg-white'
          >
            <Menu className='h-4 w-4 text-primary' />
          </button>
          {/* Desktop: collapse/expand the fixed sidebar */}
          <button
            onClick={toggleSidebar}
            aria-label='Toggle sidebar'
            className='hidden md:flex z-50 h-6 w-6 rounded-full items-center justify-center transition-colors duration-200 shadow-2xl border bg-white'
          >
            <Menu
              className={`h-3.5 w-3.5 text-primary transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}
            />
          </button>
        </>
      }
      actions={actions}
    />
  );
};

export default Header;
